import { useState, useEffect } from "react";
import {
  TextField,
  Button,
  FormGroup,
  FormControlLabel,
  Switch,
  Alert,
  Snackbar,
  Typography,
  Card,
  CardContent,
  Box,
  Divider,
  Chip,
  IconButton,
  Tooltip,
  Fade,
  Container,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Stack,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useNavigate, useLocation } from "react-router-dom";
import {
  AdminPanelSettings,
  ArrowBack,
  Save,
  Update,
  Refresh,
  Category,
  Security,
  ExpandMore,
} from "@mui/icons-material";
import AdminApiModule from "../../../components/AdminApi/Api/api";
import { permissionCategories } from "./utilis";

// Styled components with modern design
const GlassCard = styled(Card)(({ theme }) => ({
  background:
    theme.palette.mode === "dark"
      ? "rgba(25, 30, 40, 0.8)"
      : "rgba(255, 255, 255, 0.8)",
  backdropFilter: "blur(10px)",
  borderRadius: "16px",
  boxShadow:
    theme.palette.mode === "dark"
      ? "0 8px 32px 0 rgba(0, 0, 0, 0.36)"
      : "0 8px 32px 0 rgba(0, 0, 0, 0.1)",
  border:
    theme.palette.mode === "dark"
      ? "1px solid rgba(255, 255, 255, 0.1)"
      : "1px solid rgba(255, 255, 255, 0.5)",
  overflow: "hidden",
}));

const SectionHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1.5),
  marginBottom: theme.spacing(2),
  padding: theme.spacing(2.5),
  background:
    theme.palette.mode === "dark"
      ? "linear-gradient(90deg, rgba(66, 66, 255, 0.1) 0%, rgba(0, 0, 0, 0) 100%)"
      : "linear-gradient(90deg, rgba(66, 66, 255, 0.08) 0%, rgba(0, 0, 0, 0) 100%)",
  borderRadius: "12px",
  border:
    theme.palette.mode === "dark"
      ? "1px solid rgba(100, 100, 255, 0.1)"
      : "1px solid rgba(100, 100, 255, 0.08)",
}));

const PermissionCard = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: "12px",
  background:
    theme.palette.mode === "dark"
      ? "rgba(30, 35, 45, 0.5)"
      : "rgba(245, 247, 250, 0.5)",
  border:
    theme.palette.mode === "dark"
      ? "1px solid rgba(255, 255, 255, 0.05)"
      : "1px solid rgba(0, 0, 0, 0.03)",
  transition: "all 0.2s ease",
  "&:hover": {
    background:
      theme.palette.mode === "dark"
        ? "rgba(40, 45, 60, 0.6)"
        : "rgba(255, 255, 255, 0.8)",
    boxShadow: theme.shadows[2],
  },
}));

const GradientButton = styled(Button)(({ theme }) => ({
  background:
    theme.palette.mode === "dark"
      ? "linear-gradient(45deg, #667eea 0%, #764ba2 100%)"
      : "linear-gradient(45deg, #2196F3 0%, #21CBF3 100%)",
  color: "white",
  borderRadius: "10px",
  padding: "10px 24px",
  fontWeight: "600",
  boxShadow: theme.shadows[2],
  "&:hover": {
    boxShadow: theme.shadows[4],
    background:
      theme.palette.mode === "dark"
        ? "linear-gradient(45deg, #5a6fd8 0%, #6a3f9c 100%)"
        : "linear-gradient(45deg, #1976D2 0%, #00A8FF 100%)",
  },
}));

// Helper function to safely get nested values
const getNestedValue = (obj, path, defaultValue = false) => {
  if (!obj) return defaultValue;

  const keys = path.split(".");
  let result = obj;

  for (const key of keys) {
    if (result === undefined || result === null) {
      return defaultValue;
    }
    result = result[key];
  }

  return result === undefined ? defaultValue : result;
};

// Helper function to set nested values
const setNestedValue = (obj, path, value) => {
  const keys = path.split(".");
  const newObj =
    typeof structuredClone === "function"
      ? structuredClone(obj)
      : JSON.parse(JSON.stringify(obj)); // Deep clone fallback

  let current = newObj;
  for (let i = 0; i < keys.length - 1; i++) {
    const key = keys[i];
    if (current[key] === undefined) {
      current[key] = {};
    }
    current = current[key];
  }

  current[keys[keys.length - 1]] = value;
  return newObj;
};

const AddRole = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const adminApi = new AdminApiModule();

  // Initial state matching the new API structure
  const initialFormState = {
    roleName: "",
    permissions: {
      services_details: {
        full_client_access: false,
        full_primary_access: false,
        update_services_details: {
          primary_services: {
            add_services: false,
            update_services: false,
            inactive_active: false,
            view_services_list: false,
          },
          client_service: {
            add_client_service: false,
            edit_client_service: false,
            delete_client_service: false,
            view_client_service: false,
            client_ticket_acceptance: {
              add_ticket_acceptance: false,
              edit_ticket_acceptance: false,
              view_ticket_acceptance: false,
            },
          },
        },
      },
      notification_details: {
        full_access: false,
        update_notification_details: {
          add_notification: false,
          send_notification: false,
          log_notification: false,
          active_inactive: false,
          view_notification: false,
        },
      },
      center_details: {
        full_access: false,
        update_center_details: {
          add_center: false,
          delete_center: false,
          update_center: false,
          view_pincode: false,
          view_number: false,
          view_center: false,
          define_capacity: false,
          authorize_client: false,
          history_center: false,
          view_documents: false,
          download_center_list: false,
        },
      },
      ticket_details: {
        full_normal_access: false,
        update_ticket_details: {
          assigned_notassigned_ticket: false,
          delete_ticket: false,
          add_ticket: false,
          update_ticket: false,
          put_on_hold_ticket: false,
          part_request_ticket: false,
          refund_ticket_amount: false,
          part_request_ticket_list: false,
          cancel_ticket: false,
          view_normal_ticket: false,
          closed_ticket: false,
          view_center_ticket: false,
          remark_ticket: false,
          view_broadcasted_list: false,
          view_number: false,
          broadcast_ticket: false,
          view_pending_ticket: false,
          view_closed_ticket: false,
          view_all_ticket: false,
          view_e_rikshaw_ticket: false,
          view_client_ticket: false,
          view_no_service_ticket: false,
          specific_requirement_ticket: false,
          download_ticket_list: false,
          bulk_ticket_upload: false,
        },
      },
      technician_details: {
        full_access: false,
        update_technician_details: {
          add_technician: false,
          update_technician: false,
          view_document: false,
          delete_technician: false,
          authorize_client: false,
          view_number: false,
          view_technician: false,
          download_technician_list: false,
        },
      },
      clients_details: {
        full_access: false,
        update_clients_details: {
          add_clients: false,
          delete_clients: false,
          update_clients: false,
          view_pincode: false,
          view_clients: false,
        },
      },
      role_details: {
        full_access: false,
        update_role_details: {
          add_role: false,
          update_role: false,
          view_role: false,
        },
      },
      user_details: {
        full_access: false,
        update_user_details: {
          add_user: false,
          update_user: false,
          view_user: false,
          active_inactive: false,
        },
      },
      consumer_details: {
        full_access: false,
        update_user_details: {
          view_consumer: false,
        },
      },
      center_onboard_details: {
        full_access: false,
        update_center_onboard_details: {
          add_center_onboard: false,
          update_center_onboard: false,
          view_center_onboard: false,
        },
      },
      transaction_wallet_details: {
        full_access: false,
        update_transaction_wallet_details: {
          view_center_transaction: false,
          download_center_transaction: false,
          view_consumer_transaction: false,
          download_consumer_transaction: false,
          view_manual_wallet_adjustment: {
            view_user_transaction: false,
            view_client_transaction: false,
            client_recharge: false,
            consumer_technician_recharge: false,
          },
        },
      },
      banner_details: {
        full_access: false,
        update_banner_details: {
          consumer_banner: {
            view_banner: false,
            active_inactive_banner: false,
            delete_banner: false,
            upload_banner: false,
          },
          client_center_banner: {
            view_banner: false,
            active_inactive_banner: false,
            delete_banner: false,
            upload_banner: false,
          },
        },
      },
      offer_discount_details: {
        full_access: false,
        update_offer_details: {
          offer: {
            add_offer: false,
            update_offer: false,
            delete_offer: false,
            active_inactive_offer: false,
            view_offer: false,
          },
          discount: {
            add_discount: false,
            update_discount: false,
            delete_discount: false,
            active_inactive_discount: false,
            view_discount: false,
          },
        },
      },
      subscription_details: {
        full_access: false,
        subscription_details: {
          update_subscription: false,
          view_subscription: false,
        },
      },
      blog_details: {
        full_access: false,
        blog_details: {
          update_blog: false,
          view_blog: false,
          add_blog: false,
          delete_blog: false,
        },
      },
      dashboard_details: {
        full_access: false,
        update_dashboard_details: {
          view_admin_bar: false,
          view_dashboard: false,
          view_reports: false,
          view_revenue: false,
          view_performance: false,
          view_audit_logs: false,
        },
      },
    },
  };

  const [formState, setFormState] = useState(initialFormState);
  const [toast, setToast] = useState({
    open: false,
    msg: "",
    duration: 5000,
    type: "",
  });
  const [isFormValid, setIsFormValid] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const [isEditDetailsExists, setIsEditDetailsExists] = useState(false);
  const [doesRoleExists, setDoesRoleExists] = useState(false);
  const [isRoleLoading, setIsRoleLoading] = useState(false);
  const [roleFetchCompleted, setRoleFetchCompleted] = useState(false);
  const [roleId, setRoleId] = useState("");
  const [permissionSearch, setPermissionSearch] = useState("");
  const [expandedSections, setExpandedSections] = useState({});

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const editRoleId = searchParams.get("edit");

    if (editRoleId != null) {
      getRoleDetails(editRoleId);
    }
  }, [location.search]);

  const getRoleDetails = (editRoleId) => {
    const payload = {
      role_obj_id: editRoleId,
    };

    setIsEditDetailsExists(true);
    setRoleId(editRoleId);
    setIsRoleLoading(true);
    setRoleFetchCompleted(false);

    adminApi
      .getAllAvailableRoles(payload)
      .then((res) => {
        const exists = Boolean(res?.data?.length);
        setDoesRoleExists(exists);
        if (exists) updateRoleDetails(res?.data?.[0]);
        setRoleFetchCompleted(true);
        setIsRoleLoading(false);
      })
      .catch((err) => {
        console.log(err);
        setDoesRoleExists(false);
        setRoleFetchCompleted(true);
        setIsRoleLoading(false);
        showDialog(true, "error", "Unable to load role details");
      });
  };

  const updateRoleDetails = (roleDetails) => {
    setFormState({
      roleName: roleDetails?.role_name || "",
      permissions: roleDetails?.permissions || initialFormState.permissions,
    });
  };

  const showDialog = (open, type, msg) => {
    setToast({
      open: open,
      msg: msg,
      duration: 5000,
      type: type,
    });
  };

  const closeDialog = () => {
    setToast({
      open: false,
      msg: "",
      duration: 5000,
      type: "",
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormState((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handlePermissionChange = (path, value) => {
    setFormState((prev) => {
      const newPermissions = setNestedValue(prev.permissions, path, value);
      return {
        ...prev,
        permissions: newPermissions,
      };
    });
  };

  const handleFullAccessToggle = ({ category, permission, value }) => {
    // Update the clicked full_* permission itself
    handlePermissionChange(permission.path, value);

    // When enabling full access, enable all sub-permissions in the category
    if (value && category?.subSections?.length) {
      category.subSections.forEach((subSection) => {
        subSection.permissions.forEach((p) =>
          handlePermissionChange(p.path, true)
        );
      });
    }
  };

  const normalizeText = (value) =>
    String(value || "")
      .toLowerCase()
      .trim();

  const isFullPermission = (permissionKey) =>
    normalizeText(permissionKey).startsWith("full_");

  const permissionMatchesSearch = (permission) => {
    if (!permissionSearch) return true;
    return normalizeText(permission?.label).includes(
      normalizeText(permissionSearch)
    );
  };

  const categoryHasMatches = (category) => {
    if (!permissionSearch) return true;
    if (category.permissions?.some(permissionMatchesSearch)) return true;
    return Boolean(
      category.subSections?.some((s) =>
        s.permissions?.some(permissionMatchesSearch)
      )
    );
  };

  const getCategoryStats = (category) => {
    const allPermissions = [
      ...(category?.permissions || []),
      ...(category?.subSections?.flatMap((s) => s.permissions || []) || []),
    ].filter((p) => !isFullPermission(p.key));

    const total = allPermissions.length;
    const enabled = allPermissions.reduce((acc, p) => {
      const isOn = Boolean(
        getNestedValue(formState.permissions, p.path, false)
      );
      return acc + (isOn ? 1 : 0);
    }, 0);

    return { total, enabled };
  };

  const handleRegister = () => {
    const { roleName } = formState;

    if (!roleName) {
      setIsFormValid(true);
      showDialog(true, "error", "Please fill in all required details");
      return;
    }

    setIsRegistering(true);

    const payload = {
      role_name: roleName,
      permissions: formState.permissions,
    };

    console.log("Submitting payload:", payload);

    if (isEditDetailsExists) {
      payload.role_id = roleId;

      adminApi
        .adminUpdateRole(payload)
        .then((res) => {
          showDialog(true, "success", "Role updated successfully");
          setIsRegistering(false);
          navigate("/roles");
        })
        .catch((err) => {
          console.log(err);
          showDialog(true, "error", err?.response?.data?.message);
          setIsRegistering(false);
        });
    } else {
      adminApi
        .adminAddRole(payload)
        .then((res) => {
          showDialog(true, "success", "Role Created successfully");
          handleResetValues();
          navigate("/roles");
        })
        .catch((err) => {
          console.log(err);
          setIsRegistering(false);
          showDialog(true, "error", err?.response?.data?.message);
        });
    }
  };

  const handleResetValues = () => {
    setIsRegistering(false);
    setFormState(initialFormState);
  };

  const renderPermissionSection = (category) => {
    const { enabled, total } = getCategoryStats(category);
    const isExpanded = Boolean(expandedSections[category.section]);

    return (
      <Accordion
        key={category.section}
        elevation={0}
        disableGutters
        expanded={isExpanded}
        onChange={() =>
          setExpandedSections((prev) => ({
            ...prev,
            [category.section]: !prev[category.section],
          }))
        }
        sx={{
          mb: 2,
          borderRadius: 2,
          overflow: "hidden",
          border: "1px solid",
          borderColor: "divider",
          "&:before": { display: "none" },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMore />}
          sx={{
            px: 2,
            py: 1.25,
            bgcolor: (theme) =>
              theme.palette.mode === "dark"
                ? "rgba(255,255,255,0.03)"
                : "rgba(0,0,0,0.02)",
            "& .MuiAccordionSummary-content": { my: 0 },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              gap: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                minWidth: 0,
              }}
            >
              <Box
                sx={{
                  color: "primary.main",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                {category.icon}
              </Box>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 700, lineHeight: 1.2 }}
                noWrap
              >
                {category.title}
              </Typography>
            </Box>

            {total > 0 && (
              <Chip
                size="small"
                variant={enabled === total ? "filled" : "outlined"}
                color={enabled === total ? "success" : "default"}
                label={`${enabled}/${total} selected`}
                sx={{ fontWeight: 600 }}
              />
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={2.5}>
            {/* Main permissions */}
            {category.permissions?.filter(permissionMatchesSearch)?.length >
              0 && (
              <PermissionCard>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                    gap: 1,
                  }}
                >
                  {category.permissions
                    .filter(permissionMatchesSearch)
                    .map((permission) => (
                      <FormGroup key={permission.key} sx={{ m: 0 }}>
                        <FormControlLabel
                          sx={{ m: 0, justifyContent: "space-between", gap: 2 }}
                          control={
                            <Switch
                              checked={Boolean(
                                getNestedValue(
                                  formState.permissions,
                                  permission.path,
                                  false
                                )
                              )}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                if (isFullPermission(permission.key)) {
                                  handleFullAccessToggle({
                                    category,
                                    permission,
                                    value: checked,
                                  });
                                } else {
                                  handlePermissionChange(
                                    permission.path,
                                    checked
                                  );
                                }
                              }}
                              color="primary"
                            />
                          }
                          label={
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 600 }}
                            >
                              {permission.label}
                            </Typography>
                          }
                          labelPlacement="start"
                        />
                      </FormGroup>
                    ))}
                </Box>
              </PermissionCard>
            )}

            {/* Sub-sections */}
            {category.subSections
              ?.map((subSection) => ({
                ...subSection,
                permissions: (subSection.permissions || []).filter(
                  permissionMatchesSearch
                ),
              }))
              .filter((s) => s.permissions.length > 0)
              .map((subSection, subIndex) => (
                <Box
                  key={`${category.section}-${subIndex}`}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    bgcolor: (theme) =>
                      theme.palette.mode === "dark"
                        ? "rgba(255,255,255,0.02)"
                        : "rgba(255,255,255,0.65)",
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 800, mb: 1.25 }}
                  >
                    {subSection.title}
                  </Typography>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "1fr",
                        md: "1fr 1fr",
                        lg: "1fr 1fr 1fr",
                      },
                      gap: 0.75,
                    }}
                  >
                    {subSection.permissions.map((permission) => (
                      <FormGroup key={permission.key} sx={{ m: 0 }}>
                        <FormControlLabel
                          sx={{ m: 0, justifyContent: "space-between", gap: 2 }}
                          control={
                            <Switch
                              checked={Boolean(
                                getNestedValue(
                                  formState.permissions,
                                  permission.path,
                                  false
                                )
                              )}
                              onChange={(e) =>
                                handlePermissionChange(
                                  permission.path,
                                  e.target.checked
                                )
                              }
                              color="primary"
                              size="small"
                            />
                          }
                          label={
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 500 }}
                            >
                              {permission.label}
                            </Typography>
                          }
                          labelPlacement="start"
                        />
                      </FormGroup>
                    ))}
                  </Box>
                </Box>
              ))}
          </Stack>
        </AccordionDetails>
      </Accordion>
    );
  };

  if (isEditDetailsExists && isRoleLoading) {
    return (
      <Box
        sx={{
          minHeight: "60vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: 2,
        }}
      >
        <Stack spacing={2} alignItems="center">
          <CircularProgress />
          <Typography variant="body1" color="text.secondary">
            Loading role details…
          </Typography>
        </Stack>
      </Box>
    );
  }

  if (isEditDetailsExists && roleFetchCompleted && !doesRoleExists) {
    return (
      <Box
        sx={{
          minHeight: "60vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: 2,
          background: (theme) =>
            theme.palette.mode === "dark"
              ? "linear-gradient(135deg, rgba(33,150,243,0.08), rgba(103,58,183,0.06))"
              : "linear-gradient(135deg, rgba(33,150,243,0.06), rgba(103,58,183,0.04))",
        }}
      >
        <Stack spacing={1.5} alignItems="center">
          <Typography variant="h5" sx={{ fontWeight: 800 }} color="error">
            Role not found
          </Typography>
          <Typography variant="body2" color="text.secondary" align="center">
            The role you’re trying to edit doesn’t exist or you don’t have
            access.
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate("/roles")}
            startIcon={<ArrowBack />}
            sx={{ mt: 1 }}
          >
            Back to Roles
          </Button>
        </Stack>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: (theme) =>
          theme.palette.mode === "dark"
            ? "linear-gradient(135deg, rgba(33,150,243,0.10), rgba(103,58,183,0.08))"
            : "linear-gradient(135deg, rgba(33,150,243,0.06), rgba(103,58,183,0.04))",
      }}
    >
      {toast.open && (
        <Snackbar
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          open={toast.open}
          autoHideDuration={toast.duration}
          onClose={closeDialog}
          TransitionComponent={Fade}
        >
          <Alert
            onClose={closeDialog}
            severity={toast.type}
            sx={{ width: "100%" }}
            variant="filled"
          >
            {toast.msg}
          </Alert>
        </Snackbar>
      )}

      <Container maxWidth="xl" sx={{ py: 3 }}>
        {/* Header Section */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", md: "center" },
            mb: 3,
            gap: 2,
          }}
        >
          <Box sx={{ width: "100%" }}>
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 0.75 }}
            >
              <IconButton
                onClick={() => navigate("/roles")}
                size="large"
                sx={{
                  bgcolor: "background.paper",
                  boxShadow: 2,
                  "&:hover": { boxShadow: 4, bgcolor: "background.paper" },
                }}
              >
                <ArrowBack />
              </IconButton>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="h4"
                  component="h1"
                  sx={{ fontWeight: 900 }}
                >
                  {isEditDetailsExists ? "Update Role" : "Create New Role"}
                </Typography>
              </Box>
            </Box>
            <Typography variant="body1" color="text.secondary" sx={{ pl: 6.5 }}>
              {isEditDetailsExists
                ? "Modify role permissions and settings"
                : "Define a new role with specific permissions and access rights"}
            </Typography>
          </Box>

          <Chip
            icon={isEditDetailsExists ? <Update /> : <Security />}
            label={isEditDetailsExists ? "Editing Role" : "New Role"}
            color={isEditDetailsExists ? "primary" : "secondary"}
            variant="filled"
            sx={{ px: 1, py: 2.25, fontWeight: 800, boxShadow: 2 }}
          />
        </Box>

        <GlassCard>
          <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
            {/* Role Name Section */}
            <Box sx={{ mb: 4 }}>
              <SectionHeader>
                <Category sx={{ color: "primary.main" }} />
                <div>
                  <Typography variant="h6" sx={{ fontWeight: 800 }}>
                    Basic Information
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Enter the basic details for this role
                  </Typography>
                </div>
              </SectionHeader>

              <Box sx={{ px: { xs: 0.5, md: 2 } }}>
                <TextField
                  fullWidth
                  label="Role Name"
                  name="roleName"
                  value={formState.roleName}
                  onChange={handleChange}
                  error={isFormValid && !formState.roleName}
                  helperText={
                    isFormValid && !formState.roleName
                      ? "Role name is required"
                      : "Enter a descriptive name for this role"
                  }
                  variant="outlined"
                  sx={{
                    mb: 1,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "10px",
                    },
                  }}
                />
              </Box>
            </Box>

            <Divider
              sx={{
                my: 4,
                border: 0,
                height: 1,
                backgroundImage: (theme) =>
                  theme.palette.mode === "dark"
                    ? "linear-gradient(90deg, rgba(0,0,0,0), rgba(120,140,255,0.35), rgba(0,0,0,0))"
                    : "linear-gradient(90deg, rgba(0,0,0,0), rgba(25,118,210,0.25), rgba(0,0,0,0))",
              }}
            />

            {/* Permissions Sections */}
            <Box sx={{ mb: 4 }}>
              <SectionHeader>
                <AdminPanelSettings sx={{ color: "primary.main" }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="h6" sx={{ fontWeight: 800 }}>
                    Permissions
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Select the permissions for this role
                  </Typography>
                </Box>

                <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() =>
                      setExpandedSections(
                        permissionCategories.reduce(
                          (acc, c) => ({ ...acc, [c.section]: true }),
                          {}
                        )
                      )
                    }
                    sx={{ borderRadius: 2, fontWeight: 700 }}
                  >
                    Expand all
                  </Button>
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => setExpandedSections({})}
                    sx={{ borderRadius: 2, fontWeight: 700 }}
                  >
                    Collapse all
                  </Button>
                </Stack>
              </SectionHeader>

              <Stack spacing={2} sx={{ px: { xs: 0.5, md: 1 } }}>
                <TextField
                  fullWidth
                  value={permissionSearch}
                  onChange={(e) => setPermissionSearch(e.target.value)}
                  label="Search permissions"
                  placeholder="Type to filter by name…"
                  variant="outlined"
                  size="small"
                  sx={{
                    "& .MuiOutlinedInput-root": { borderRadius: 2 },
                  }}
                />

                {permissionCategories.filter(categoryHasMatches).length ===
                0 ? (
                  <Box
                    sx={{
                      p: 3,
                      borderRadius: 2,
                      border: "1px dashed",
                      borderColor: "divider",
                      bgcolor: "background.paper",
                    }}
                  >
                    <Typography sx={{ fontWeight: 800 }}>No matches</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Try a different search term.
                    </Typography>
                  </Box>
                ) : (
                  permissionCategories
                    .filter(categoryHasMatches)
                    .map(renderPermissionSection)
                )}
              </Stack>
            </Box>

            {/* Action Buttons */}
            <Box
              sx={{
                mt: 4,
                pt: 3,
                borderTop: "1px solid",
                borderColor: "divider",
              }}
            >
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.5}
                sx={{ alignItems: { sm: "center" } }}
              >
                <Tooltip
                  title={
                    isEditDetailsExists ? "Update this role" : "Create new role"
                  }
                >
                  <span>
                    <GradientButton
                      onClick={handleRegister}
                      disabled={isRegistering}
                      startIcon={isEditDetailsExists ? <Update /> : <Save />}
                      sx={{ width: { xs: "100%", sm: "auto" } }}
                    >
                      {isRegistering
                        ? "Processing..."
                        : isEditDetailsExists
                        ? "Update Role"
                        : "Create Role"}
                    </GradientButton>
                  </span>
                </Tooltip>

                <Button
                  variant="outlined"
                  onClick={() => navigate("/roles")}
                  size="large"
                  startIcon={<ArrowBack />}
                  sx={{
                    borderRadius: 2,
                    fontWeight: 800,
                    width: { xs: "100%", sm: "auto" },
                  }}
                >
                  Cancel
                </Button>

                {!isEditDetailsExists && (
                  <Button
                    variant="text"
                    onClick={handleResetValues}
                    size="large"
                    startIcon={<Refresh />}
                    sx={{
                      borderRadius: 2,
                      fontWeight: 800,
                      width: { xs: "100%", sm: "auto" },
                    }}
                  >
                    Reset
                  </Button>
                )}
              </Stack>
            </Box>
          </CardContent>
        </GlassCard>
      </Container>
    </Box>
  );
};

export default AddRole;
